import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Checkbox,
  ClickAwayListener,
  Paper,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { showToast } from "../../../../utils/ToastEmitter";
import useAuthValue from "../../../authModule/hooks/useAuthValue";
import useSelectedHospitalValue from "../../hooks/useSelectedHospitalValue";
import useDebounce from "../../../../hooks/useDebounce";
import { useTranslation } from "react-i18next";

interface OptionType {
  key: string;
  value: string;
}

interface HospitalSelectorDropdownProps {
  anchorEl: HTMLElement | null;
  options: OptionType[];
  onChange: (selected: { key: string; value: string }) => void;
  onClose: () => void;
}

const HospitalSelectorDropdown: React.FC<HospitalSelectorDropdownProps> = ({
  anchorEl,
  options,
  onChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const { hospitalId } = useAuthValue();
  const selectedHospitalInfo = useSelectedHospitalValue();

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    if (!selectedHospitalInfo?.uuid) {
      const matched = options.find((opt) => opt.key === hospitalId);
      if (matched) {
        onChange(matched);
      }
    }
  }, [hospitalId, onChange, options, selectedHospitalInfo?.uuid]);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      opt.value.toLowerCase().includes(debouncedSearchText.toLowerCase())
    );
  }, [debouncedSearchText, options]);

  const handleToggle = (key: string) => {
    const selected = options.find((opt) => opt.key === key);
    if (selected && selected.key !== selectedHospitalInfo?.uuid) {
      onChange(selected);
      showToast(
        t("Change_hospital_Success_Message", {
          hospitalName: selected.value,
        })
      );
    }
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={{ mt: 1.5 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            mt: 0,
            p: 1,
            borderRadius: "8px",
            minWidth: 200,
            maxHeight: 300,
            overflowY: "auto",
            boxShadow: `0px 5px 15px rgba(0, 0, 0, 0.17), 0px 2px 5px rgba(0, 0, 0, 0.12)`,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search..."
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              mb: 1,
              "& .MuiOutlinedInput-root": {
                background: "#F3F3F3",
                borderRadius: "4px",
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": {
                  border: "none",
                },
              },
            }}
          />
          {filteredOptions.map((option) => (
            <Box
              key={option.key}
              display="flex"
              alignItems="center"
              gap={1}
              px={1}
              py={0.5}
              sx={{ cursor: "pointer" }}
              onClick={() => handleToggle(option.key)}
            >
              <Checkbox
                checked={selectedHospitalInfo?.uuid === option.key}
                sx={{
                  color: "#000",
                  "&.Mui-checked": { color: "#65558F" },
                  p: 0.5,
                }}
              />
              <Typography fontSize={14}>{option.value}</Typography>
            </Box>
          ))}
        </Paper>
      </ClickAwayListener>
    </Popover>
  );
};

export default HospitalSelectorDropdown;
